import {useMutation, useQueryClient} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';

type CreateClubBody = {
  name: string;
  description?: string;
  badge_id?: string;
  background_color?: string;
  visibility: 'public' | 'private';
  join_policy: 'open' | 'approval' | 'invite_only';
  searchable: boolean;
  min_rating?: number | null;
};

export const useCreateClub = () => {
  const qc = useQueryClient();
  return useMutation(
    async (body: CreateClubBody) =>
      (await ChessRestClient.post('/api/clubs/', body)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(['clubs', 'mine']);
      },
    },
  );
};

const clubScoped = (clubId: number, path: string) =>
  `/api/clubs/${clubId}/${path}`;

const useClubMutation = <TVars>(
  clubId: number,
  fn: (vars: TVars) => Promise<unknown>,
  extraKeys: unknown[][] = [],
) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      qc.invalidateQueries(['clubs', 'detail', clubId]);
      extraKeys.forEach(k => qc.invalidateQueries(k));
    },
  });
};

export const useJoinClub = (clubId: number) =>
  useClubMutation(
    clubId,
    async () =>
      (await ChessRestClient.post(clubScoped(clubId, 'join/'), {})).data,
    [['clubs', 'mine']],
  );

export const useDecideRequest = (clubId: number) =>
  useClubMutation(
    clubId,
    async (v: {requestId: number; action: 'approve' | 'reject'}) =>
      (
        await ChessRestClient.post(
          clubScoped(clubId, `requests/${v.requestId}/`),
          {action: v.action},
        )
      ).data,
    [['clubs', 'requests', clubId]],
  );

export const useCreateInvite = (clubId: number) =>
  useClubMutation(
    clubId,
    async (
      v: {channel?: string; invitee_contact?: string; note?: string} = {},
    ) => (await ChessRestClient.post(clubScoped(clubId, 'invites/'), v)).data,
    [['clubs', 'invites', clubId]],
  );

export const useRevokeInvite = (clubId: number) =>
  useClubMutation(
    clubId,
    async (code: string) =>
      (
        await ChessRestClient.post(
          clubScoped(clubId, `invites/${code}/revoke/`),
          {},
        )
      ).data,
    [['clubs', 'invites', clubId]],
  );

export const useRedeemInvite = () => {
  const qc = useQueryClient();
  return useMutation(
    async (code: string) =>
      (await ChessRestClient.post(`/api/invites/${code}/redeem/`, {})).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(['clubs', 'mine']);
      },
    },
  );
};

export const useMemberAction = (clubId: number) =>
  useClubMutation(
    clubId,
    async (v: {userId: number; action: 'remove' | 'promote' | 'demote'}) =>
      (
        await ChessRestClient.post(clubScoped(clubId, `members/${v.userId}/`), {
          action: v.action,
        })
      ).data,
  );

export const useSetAnnouncement = (clubId: number) =>
  useClubMutation(
    clubId,
    async (text: string) =>
      (await ChessRestClient.post(clubScoped(clubId, 'announcement/'), {text}))
        .data,
  );

export const useSetRules = (clubId: number) =>
  useClubMutation(
    clubId,
    async (text: string) =>
      (await ChessRestClient.post(clubScoped(clubId, 'rules/'), {text})).data,
    [['clubs', 'detail', clubId]],
  );

export const useSetPreset = (clubId: number) =>
  useClubMutation(
    clubId,
    async (presetId: number) =>
      (
        await ChessRestClient.post(clubScoped(clubId, 'preset/'), {
          preset_id: presetId,
        })
      ).data,
  );

// Staff-only: change a club's status (active/disabled/banned/archived/pending_review).
// Backend gates on is_staff (403 not_staff otherwise). Refreshes club detail + list.
export const useSetClubStatus = (clubId: number) =>
  useClubMutation(
    clubId,
    async (status: string) =>
      (await ChessRestClient.post(clubScoped(clubId, 'status/'), {status}))
        .data,
    [['clubs', 'mine'], ['clubs']],
  );

export const useLeaveClub = (clubId: number) =>
  useClubMutation(
    clubId,
    async (replacementAdminId?: number) =>
      (
        await ChessRestClient.post(
          clubScoped(clubId, 'leave/'),
          replacementAdminId == null
            ? {}
            : {replacement_admin_id: replacementAdminId},
        )
      ).data,
    [['clubs', 'mine']],
  );
